import { History } from "@/components/templates/history";
import { Voicemail } from "@/components/templates/voicemail";
import { NavigationPanel } from "@/components/organisms/navigation";
import {
	NavigationActions,
	NavigationState,
	TabType,
	ToggleTabType,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { ContactsPanel } from "@/components/organisms/contacts";
import { Position } from "@/common/theme/styleUtils";
import { useStyle } from "./style";
import { ResourcesPanel } from "@/components/organisms/resources";
import { NewInteractionPanel } from "@/components/organisms/newInteraction";
import { Supervisor } from "@/components/templates/supervisor";
import { useQueryClient } from "@tanstack/react-query";
import { Agent } from "@/components/templates/agent";
import { AllInteractions } from "@/components/templates/allInteractions";
import {
	ContainerSizeDefaults,
	WindowWidthThresholds,
} from "@/common/constants";
import { ResizableContainer } from "@/components/atoms/resizableContainer";
import { useResizeHook } from "@/hooks/resize";
import { ResizeHint } from "@/components/atoms/resizeHint";
import { TopBar } from "@/components/organisms/topbar";
import { AicLockBanner } from "@/components/atoms/aicLockBanner";
import { app as microsoftApp } from "@microsoft/teams-js";
import { StatisticsPanel } from "@/components/organisms/statistics/StatisticsPanel";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import {
	AppCacheActions,
	AppSettingsState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { BREAKPOINTS, useResponsiveBreakpoints } from "@/hooks/responsive";
import { HistoryPanelStashActions } from "@/common/stores/nonpersistingStores/history/types";
import { useHistoryPanelStashStore } from "@/common/stores/nonpersistingStores/history/historyPanelStashStore";
import { InteractionCard } from "@/components/organisms/interaction";
import { useAgentPermissionsStore } from "@/common/stores/persistingStores/agentPermissions/agentPermissionsStore";
import { AgentPermissionsState } from "@/common/stores/persistingStores/agentPermissions";
import { useContactsAdvancedFilterStore } from "@/common/stores/nonpersistingStores/contactsAdvancedFilter/contactsAdvancedFilterStore";
import { ContactsAdvancedFilterActions } from "@/common/stores/nonpersistingStores/contactsAdvancedFilter/types";
import { useGlobalHotkey } from "@/hooks/hotkey";
import { KeyCode } from "@/common/constants/keyValue";

export const HomeView = () => {
	const classes = useStyle();
	const selectedTab = useNavigationStore(
		(state: NavigationState) => state.selectedTab
	);
	const toggledTab = useNavigationStore(
		(state: NavigationState) => state.toggledTab
	);

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const setIsMobileDetailViewMode = useNavigationStore(
		(state: NavigationActions) => state.setIsMobileDetailViewMode
	);

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const queryClient = useQueryClient();

	const canUseAdvancedContactFilter = useAgentPermissionsStore(
		(state: AgentPermissionsState) =>
			state.isSupervisor || state.hasOperatorRights
	);

	const setAdvancedContactFilterDialogOpen = useContactsAdvancedFilterStore(
		(state: ContactsAdvancedFilterActions) => state.setDialogOpen
	);

	const openAdvancedContactFilter = useCallback(() => {
		setToggledTab(ToggleTabType.Contacts);
		setAdvancedContactFilterDialogOpen(true);
	}, [setToggledTab, setAdvancedContactFilterDialogOpen]);

	useGlobalHotkey(
		{ code: KeyCode.F, alt: true, shift: true },
		openAdvancedContactFilter,
		canUseAdvancedContactFilter
	);

	const { isMobile, isTablet } = useResponsiveBreakpoints();

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const restoreToggledTab = useHistoryPanelStashStore(
		(state: HistoryPanelStashActions) => state.restoreToggledTab
	);

	const prevWidthRef = useRef(window.innerWidth);

	useEffect(() => {
		if (selectedTab === TabType.History) {
			queryClient.invalidateQueries({
				queryKey: ["history"],
			});
		}

		// Leaving History unmounts the detail view, so restore the panel it hid. The
		// stash is already empty when the panel was closed or switched by anything
		// else, so nothing is restored in those cases.
		if (selectedTab !== TabType.History) {
			restoreToggledTab();
		}
	}, [selectedTab]);

	useEffect(() => {
		const handleResize = () => {
			const currentWidth = window.innerWidth;

			if (
				currentWidth < prevWidthRef.current &&
				currentWidth <= BREAKPOINTS.tablet
			) {
				resetToMinSize();
				setToggledTab();
			}

			prevWidthRef.current = currentWidth;
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!isMobile) {
			setIsMobileDetailViewMode(false);
		}
	}, [isMobile]);

	useEffect(() => {
		if (toggledTab) {
			setIsMobileDetailViewMode(false);
		}
	}, [toggledTab]);

	const resetToMinSize = () => {
		appStoreActions.setCache(
			CacheKey.InteractionPanelWidth,
			ContainerSizeDefaults.InteractionPanelWidth
		);
		appStoreActions.setCache(
			CacheKey.ToggledViewPanelWidth,
			ContainerSizeDefaults.ToggledViewPanelMinWidth
		);
		appStoreActions.setCache(
			CacheKey.AllWaitingInteractionsPanelWidth,
			ContainerSizeDefaults.AllWaitingInteractionsMinWidth
		);
	};

	const views = useMemo<{ [key: number]: JSX.Element }>(() => {
		return {
			[TabType.Interactions]: <Agent />,
			[TabType.History]: <History />,
			[TabType.Voicemail]: <Voicemail />,
			[TabType.Supervisor]: <Supervisor />,
			[TabType.SupervisorInteractions]: <AllInteractions />,
		};
	}, [toggledTab]);

	const [intPanelWidth, onResizeStop] = useResizeHook(
		CacheKey.ToggledViewPanelWidth,
		window.innerWidth > WindowWidthThresholds.Large
			? ContainerSizeDefaults.ToggledViewPanelDefaultWidth
			: ContainerSizeDefaults.ToggledViewPanelMinWidth
	);

	const toggleViews = useMemo(
		() => ({
			[ToggleTabType.Contacts]: <ContactsPanel shadowTo={Position.Left} />,
			[ToggleTabType.Resources]: <ResourcesPanel shadowTo={Position.Left} />,
			...(appSettings.queueStatisticsEnabled && {
				[ToggleTabType.Statistics]: (
					<StatisticsPanel shadowTo={Position.Left} />
				),
			}),
			[ToggleTabType.NewInteraction]: (
				<NewInteractionPanel shadowTo={Position.Left} />
			),
		}),
		[appSettings.queueStatisticsEnabled]
	);

	const maxWidth = useMemo(() => {
		if (isTablet) {
			return ContainerSizeDefaults.PanelsMaxWidthOnTablet;
		}

		return ContainerSizeDefaults.ToggledViewPanelMaxWidth;
	}, [isTablet]);

	const resizableContainerWithView = useCallback(
		(tabType: ToggleTabType) => {
			const component = toggleViews[tabType];
			if (!component) {
				return null;
			}

			return (
				<ResizableContainer
					minWidth={ContainerSizeDefaults.ToggledViewPanelMinWidth}
					maxWidth={maxWidth}
					width={intPanelWidth}
					enabledSides={["left"]}
					handleStyles={{ left: { left: "0px", width: "1.5px" } }}
					handleComponent={{
						left: <ResizeHint className={classes.resizeHintLeft} />,
					}}
					onResizeStop={onResizeStop}
				>
					{component}
				</ResizableContainer>
			);
		},
		[intPanelWidth, toggleViews, classes.resizeHintLeft, onResizeStop, maxWidth]
	);

	const tabsRouter = useCallback(
		(tab: TabType) => {
			if (!isMobile || toggledTab === undefined) {
				return views[tab];
			}

			return null;
		},
		[views, isMobile, toggledTab]
	);

	const toggleRouter = useCallback(
		(tab?: ToggleTabType) => {
			if (tab !== undefined && toggleViews[tab]) {
				return isMobile ? toggleViews[tab] : resizableContainerWithView(tab);
			}
			return null;
		},
		[toggleViews, intPanelWidth, selectedTab, isMobile]
	);

	return (
		<>
			<div className={classes.container}>
				{!microsoftApp.isInitialized() && <TopBar />}
				<AicLockBanner />
				<NavigationPanel />
				<div className={classes.tabsAndToggledViewsContainer}>
					{tabsRouter(selectedTab)}
					{toggleRouter(toggledTab)}
				</div>
			</div>
			{isMobile && <InteractionCard />}
		</>
	);
};
